import React, { useEffect, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import SearchMenu from "../GenericComponents/SearchMenu";
import ChatListElement from "./ChatListElement";
import { filterConversations } from "../../Helpers/filters/filterConversations";
import { useContextMenu } from "react-contexify";
import { colors } from "../../Assets/styles/colors";
import ConversationListContextMenu from "./ConversationListContextMenu";
import { useConversationService } from "../../Context/ConversationsContext";
import { useTranslate } from '@tolgee/react';
import DeleteConfirmationChild from "../GenericComponents/DeleteConfirmationChild";
import FirstTimeUsingMessage from "../GenericComponents/FirstTimeUsingMessage";
import LocalStorage from "../../Helpers/LocalStorage";
import TryNowProCard from "../GenericComponents/TryNoewProCard";
import moment from "moment";
import { RotatingLines } from "react-loader-spinner";
import { useAuthService } from "../../Context/AuthContext";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useNotify } from "../../Helpers/Notifications";
import EmptyChatList from "./EmptyChatList";

const ConversationsList = ({
  searchInput,
  setSearchInput,
  conversations,
  selectedConversation,
  handleSelectConversation,
  loadConversations,
  handleOpenSubscriptionModal,
  getDraftMessage,
  hasDraftMessage,
  getReplyState,
  hasReplyState,
}) => {
  const { user, subscriptionLevel } = useAuthService();
  const { t } = useTranslate();
  const { setLoading, messagesListSpinner } = useGenericComponentsStateService();
  const { markMessagesAsRead, hideConversation, deleteConversation } = useConversationService();
  
  const [selectedContextMenuItem, setSelectedContextMenuItem] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  
  const [showFirstUseMessage, setShowFirstUseMessage] = useState(
    LocalStorage.getFirstMessageRead()
  );
  const [showTryProCard, setShowTryProCard] = useState(false);
  const { notifySuccess, notifyError } = useNotify();
  const MENU_ID = "conversationsList-id";

  // REFACTOR: Use state for blocked IDs to trigger re-renders naturally
  const [blockedIds, setBlockedIds] = useState(new Set());

  const { show } = useContextMenu({
    id: MENU_ID,
  });

  const selectedConversationRef = useRef(selectedConversation);
  
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  function displayMenu(e, conversation) {
    setSelectedContextMenuItem(conversation);
    show({ event: e });
  }
  
  const handleDeleteConversation = (arg) => {
    let conversationId = null;

    if (arg && typeof arg === 'object' && arg.preventDefault) {
        arg.preventDefault();
        arg.stopPropagation();
        conversationId = selectedContextMenuItem?.id;
    } else if (arg) {
        conversationId = arg;
    } else {
        conversationId = selectedContextMenuItem?.id;
    }

    if (!conversationId) {
        console.error("No Conversation ID found to delete.");
        return;
    }

    // OPTIMISTIC UPDATE: Hide immediately using state
    setBlockedIds(prev => {
        const next = new Set(prev);
        next.add(conversationId);
        return next;
    });

    setLoading(true);
    
    deleteConversation(
      conversationId,
      (response) => {
        const currentId = selectedConversationRef.current?.id;
        
        if (currentId && String(currentId) === String(conversationId)) {
           handleSelectConversation(null);
        }
        
        setLoading(false);
        setDeleteConfirmationOpen(false);
        setSelectedContextMenuItem(null);

        // REFACTOR: Removed timeout. Optimistic UI handles the view, 
        // loadConversations handles the data sync.
        if (loadConversations) loadConversations();
      },
      (error) => {
        console.log(error);
        notifyError(t('general.something_wrong'));
        
        // ROLLBACK: Unblock ID on failure
        setBlockedIds(prev => {
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
        });
        
        setLoading(false);
      }
    );
  };

  const handleHideConversation = (conversationId) => {
    // OPTIMISTIC UPDATE: Hide immediately using state
    setBlockedIds(prev => {
        const next = new Set(prev);
        next.add(conversationId);
        return next;
    });

    setLoading(true);

    hideConversation(
      conversationId,
      (response) => {
        const currentId = selectedConversationRef.current?.id;
        if (currentId && String(currentId) === String(conversationId)) {
          handleSelectConversation(null);
        }

        setLoading(false);

        // REFACTOR: Removed timeout
        if (loadConversations) loadConversations();
      },
      (error) => {
        console.log(error);
        notifyError(t('general.something_wrong'));
        
        // ROLLBACK: Unblock ID on failure
        setBlockedIds(prev => {
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
        });
        
        setLoading(false);
      }
    );
  };

  const readConversationMessages = (id) => {
    let conversation = conversations.find(
      (conversation) => conversation.id === id
    );
    if (conversation && conversation.unread_messages_ids.length > 0) {
      markMessagesAsRead(
        id,
        conversation.unread_messages_ids,
        function (response) { },
        function (error) {
          console.log(error, "error mark as read");
        }
      );
    }
  };

  useEffect(() => {
    if (!user) return;
    
    if (user.is_pro || subscriptionLevel === "platform") {
      setShowTryProCard(false);
    } else {
      let date = LocalStorage.getTryProCard();

      if (date) {
        let lastCloseData = moment(date);
        const oneWeekAgo = moment().subtract(1, "weeks");
        if (lastCloseData.isBefore(oneWeekAgo)) {
          setShowTryProCard(true);
        } else {
          setShowTryProCard(false);
        }
      } else {
        setShowTryProCard(true);
      }
    }
  }, [user, subscriptionLevel]);

  // REFACTOR: Added blockedIds to dependency array
  const visibleConversations = useMemo(() => {
    if (!conversations) return [];
    
    // Check against state-based set
    const safeList = conversations.filter(c => !blockedIds.has(c.id));
    
    return filterConversations(safeList, searchInput);
  }, [conversations, searchInput, blockedIds]);

  return (
    <ChatsList>
      <DeleteConfirmationChild
        setOpen={setDeleteConfirmationOpen}
        open={deleteConfirmationOpen}
        action={handleDeleteConversation}
        item={selectedContextMenuItem?.id}
        variant={"delete_chat"}
      />

      <ConversationListContextMenu
        selectedContextMenuItem={selectedContextMenuItem}
        handleDeleteConversation={handleDeleteConversation}
        setDeleteConfirmationOpen={setDeleteConfirmationOpen}
        handleHideConversation={handleHideConversation}
        readConversationMessages={readConversationMessages}
      />
      
      <div style={{ padding: "16px" }}>
        <SearchMenu value={searchInput} setvalue={setSearchInput} />
      </div>
      {!showFirstUseMessage && (
        <FirstTimeUsingMessage
          setShowFirstUseMessage={setShowFirstUseMessage}
        />
      )}
      {showTryProCard && (
        <TryNowProCard
          setShowTryProCard={setShowTryProCard}
          handleOpenSubscriptionModal={handleOpenSubscriptionModal}
        />
      )}

      {!messagesListSpinner ? (
        visibleConversations?.map(
          (conversation, index) => (
            <ChatListElement
              key={conversation.id || index}
              data={conversation}
              selected={selectedConversation?.id === conversation.id}
              handleSelectConversation={handleSelectConversation}
              displayMenu={displayMenu}
              getDraftMessage={getDraftMessage}
              hasDraftMessage={hasDraftMessage}
              getReplyState={getReplyState}
              hasReplyState={hasReplyState}
            />
          )
        )
      ) : (
        <SpinnerWrapper>
          <RotatingLines
            strokeColor={colors.purple}
            strokeWidth={3}
            animationDuration="0.75"
            width={50}
            visible={true}
          />
        </SpinnerWrapper>
      )}

      {visibleConversations.length === 0 && (
        <EmptyChatList />
      )}
    </ChatsList>
  );
};

export default ConversationsList;

const ChatsList = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: 100%;
  width: 100%;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;